import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ListTree } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui';
import { request } from '@/lib/api';
import { taskListSchema } from '@/lib/schemas';
import { cn, formatDate, formatDateTime } from '@/lib/utils';
import { TaskStatusBadge } from './task-status';

/**
 * המשימות שנפתחו מאותה הצעה חתומה: ביקורים ומשימות ניהול, לפי הסדר —
 * קודם מה שהמנהל צריך להכין, אחר כך הביקורים.
 */
export function WorkPlanCard({ quoteId, currentTaskId }: { quoteId: string; currentTaskId: string }) {
  const { t } = useTranslation('tasks');
  const tasks = useQuery({
    queryKey: ['tasks', { originQuoteId: quoteId }],
    queryFn: ({ signal }) => request(`/tasks?originQuoteId=${quoteId}&take=50`, { schema: taskListSchema, signal }),
  });
  const items = [...(tasks.data?.items ?? [])].sort(
    (a, b) =>
      (a.kind === 'ADMIN' ? 0 : 1) - (b.kind === 'ADMIN' ? 0 : 1) ||
      (a.visitIndex ?? 0) - (b.visitIndex ?? 0) ||
      (a.dueAt ?? a.scheduledStart ?? '').localeCompare(b.dueAt ?? b.scheduledStart ?? ''),
  );
  if (items.length <= 1) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ListTree className="size-4 text-accent" aria-hidden />
          {t('workPlan.title')}
        </CardTitle>
        <CardDescription>{t('workPlan.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="space-y-1.5">
          {items.map((task) => (
            <li key={task.id}>
              <Link
                to="/tasks/$taskId"
                params={{ taskId: task.id }}
                className={cn(
                  'flex items-start justify-between gap-2 rounded-(--radius-md) px-2 py-1.5 text-xs hover:bg-surface-sunken',
                  task.id === currentTaskId && 'bg-accent-subtle',
                )}
                aria-current={task.id === currentTaskId ? 'page' : undefined}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-fg">{task.title}</span>
                  <span className="block text-2xs text-fg-muted">
                    {task.kind === 'ADMIN'
                      ? `${t('workPlan.forManager')}${task.dueAt ? t('workPlan.dueSuffix', { date: formatDate(task.dueAt) }) : ''}`
                      : task.scheduledStart
                        ? `${formatDateTime(task.scheduledStart)}${task.assignedTo?.name ? ` · ${task.assignedTo.name}` : ''}`
                        : t('workPlan.notScheduled')}
                  </span>
                </span>
                {task.kind === 'ADMIN' && task.status !== 'CLOSED' ? <Badge tone="info">{t('workPlan.forManager')}</Badge> : <TaskStatusBadge status={task.status} />}
              </Link>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
